// Shared color palettes for admin dashboard telemetry charts.
// See docs/admin-dashboard-style-guide.md for which palette each chart shape uses.

// Four distinct, accessible colors readable against the dashboard's dark (#1a1a1a) background.
// soft purple, teal, amber, rose.
export const CATEGORICAL_4 = ['#8B5CF6', '#2DD4BF', '#F59E0B', '#F472B6'];

// Neutral gray reserved for the aggregated "Other" / "unknown" segment.
export const OTHER_COLOR = '#6B7280';

// Dashboard primary accent, used as the default single-series bar color.
export const SINGLE_DEFAULT = '#8B5CF6';

// The dimension value that is always rendered in neutral gray and stacked last.
export const OTHER_LABEL = 'Other';

export type PaletteName =
  | 'categorical-4'
  | 'categorical-5-with-other'
  | 'categorical-many';

// Deterministic 32-bit string hash (not cryptographic) — same input always yields the
// same number, so a username/dimension keeps its color across reloads and period switches.
export const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (Math.imul(31, hash) + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

// Map any string to a stable, readable HSL color: hsl(hash % 360, 70%, 60%).
export const colorFromString = (value: string): string =>
  `hsl(${hashString(value) % 360}, 70%, 60%)`;

/**
 * Resolve a color for each segment. `palette` may be a named palette or an explicit
 * { segment: hex } map (used when a fixed color must match across multiple charts).
 * "Other" is always neutral gray regardless of palette.
 */
export const resolveSegmentColors = (
  segments: string[],
  palette: PaletteName | Record<string, string>
): Record<string, string> => {
  const colors: Record<string, string> = {};

  if (typeof palette === 'object') {
    segments.forEach((seg) => {
      colors[seg] = seg === OTHER_LABEL ? OTHER_COLOR : palette[seg] ?? colorFromString(seg);
    });
    return colors;
  }

  if (palette === 'categorical-many') {
    segments.forEach((seg) => {
      colors[seg] = seg === OTHER_LABEL ? OTHER_COLOR : colorFromString(seg);
    });
    return colors;
  }

  // categorical-4 and categorical-5-with-other both draw non-Other segments from
  // CATEGORICAL_4 by order; the only difference is the explicit gray Other slot.
  let i = 0;
  segments.forEach((seg) => {
    if (seg === OTHER_LABEL) {
      colors[seg] = OTHER_COLOR;
      return;
    }
    colors[seg] = CATEGORICAL_4[i % CATEGORICAL_4.length] ?? colorFromString(seg);
    i++;
  });
  return colors;
};
